import { Injectable } from '@angular/core';
import { SyllabusHttpModel } from '@examdojo/category';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { QuestionHttpModel, QuestionItemV2 } from '@examdojo/models/question';
import { UserHttpModel } from '@examdojo/models/user';
import { Feedback, QuestionEntityHttpService } from '@examdojo/question';
import { ReferenceQuestionHttpModel, ReferenceQuestionHttpService } from '@examdojo/reference-question';
import { rethrowError } from '@examdojo/rxjs';
import { TableRowModel } from '@examdojo/supabase';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, combineLatest, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { CurrentReviewHttpService } from '../current-review-http.service';
import { ReviewItemHttpService } from './review-item-http.service';
import {
  DerivativeQuestionReviewType,
  ReferenceQuestionHistoryEntry,
  ReferenceQuestionReviewType,
  ReviewItemHttpModel,
} from './review-item.model';
import { ReviewItemStore } from './review-item.store';

type ReviewItemId = ReviewItemHttpModel['id'];

@Injectable({ providedIn: 'root' })
export class ReviewItemService {
  constructor(
    private readonly currentReviewHttpService: CurrentReviewHttpService,
    private readonly reviewItemHttpService: ReviewItemHttpService,
    private readonly store: ReviewItemStore,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly referenceQuestionHttpService: ReferenceQuestionHttpService,
    private readonly questionHttpService: QuestionEntityHttpService,
  ) {}

  readonly isSendingFeedback$$ = new BehaviorSubject<boolean>(false);

  fetchActive() {
    return this.currentReviewHttpService.fetchAll().pipe(
      switchMap((data) => {
        const firstItem = data[0];
        if (!firstItem) {
          this.store.updateProp('session', undefined);
          return of(null);
        }

        this.store.updateProp('session', firstItem.review_session);
        return this.reviewItemHttpService.fetch(firstItem.reviewable_item_id).pipe(map((item) => item));
      }),
      tap((item) => {
        if (item) {
          this.store.upsertMany([item]);
          this.store.setActive(item.id);
        } else {
          this.store.setActive(null);
        }
      }),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.review_item' }),
    );
  }

  getNextReferenceQuestionReviewItem(reviewType: ReferenceQuestionReviewType, syllabusId: SyllabusHttpModel['id']) {
    return this.currentReviewHttpService.getNextReferenceQuestion(reviewType, syllabusId).pipe(
      switchMap(() => this.fetchActive()),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.review_item' }),
    );
  }

  getNextQuestionReviewItem(reviewType: DerivativeQuestionReviewType, syllabusId: SyllabusHttpModel['id']) {
    return this.currentReviewHttpService.getNextQuestion(reviewType, syllabusId).pipe(
      switchMap(() => this.fetchActive()),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.review_item' }),
    );
  }

  skipReviewItem(userId: UserHttpModel['id'], reviewItemId: ReviewItemId) {
    return this.currentReviewHttpService.skipQuestion(userId, reviewItemId).pipe(
      tap(() => this.store.setActive(null)),
      switchMap(() => this.fetchActive()),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.review_item', action: 'skip' }),
    );
  }

  cancelQuestion(reviewItemId: ReviewItemId) {
    return this.currentReviewHttpService.cancelQuestion(reviewItemId).pipe(
      tap(() => this.store.setActive(null)),
      switchMap(() => this.fetchActive()),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.review_item', action: 'cancel' }),
    );
  }

  giveQuestionFeedback(questionId: QuestionHttpModel['id'], feedback: Feedback[]) {
    this.isSendingFeedback$$.next(true);

    return this.reviewItemHttpService.giveQuestionFeedback(questionId, feedback).pipe(
      // tap(() => this.store.setActive(null)),
      // switchMap(() => this.fetchActive()),
      finalize(() => this.isSendingFeedback$$.next(false)),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.feedback', action: 'create' }),
    );
  }

  giveReferenceQuestionFeedback(referenceQuestionId: ReferenceQuestionHttpModel['id'], feedback: Feedback[]) {
    this.isSendingFeedback$$.next(true);

    return this.reviewItemHttpService.giveReferenceQuestionFeedback(referenceQuestionId, feedback).pipe(
      // tap(() => this.store.setActive(null)),
      // switchMap(() => this.fetchActive()),
      finalize(() => this.isSendingFeedback$$.next(false)),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.feedback', action: 'create' }),
    );
  }

  getReferenceQuestionFeedback(referenceQuestionId: ReferenceQuestionHttpModel['id']) {
    return this.reviewItemHttpService
      .getReferenceQuestionFeedback(referenceQuestionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.feedback', action: 'fetch' }));
  }

  getQuestionFeedback(questionId: QuestionHttpModel['id']) {
    return this.reviewItemHttpService
      .getQuestionFeedback(questionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.feedback', action: 'fetch' }));
  }

  /**
   * Retrieves comprehensive history for a reference question including:
   * - Edit history from reference_questions_history
   * - Status change snapshots from reference_question_snapshots
   * - Review feedback with snapshots
   * @param referenceQuestionId - The ID of the reference question
   * @returns Observable with array of unified history entries, sorted by timestamp (newest first)
   */
  getReferenceQuestionComprehensiveHistory(
    referenceQuestionId: ReferenceQuestionHttpModel['id'],
  ): Observable<ReferenceQuestionHistoryEntry[]> {
    const editHistory$ = this.referenceQuestionHttpService.fetchEditHistory(referenceQuestionId);
    const statusSnapshots$ = this.referenceQuestionHttpService.fetchStatusSnapshots(referenceQuestionId);
    const reviewFeedback$ = this.getReferenceQuestionFeedback(referenceQuestionId);
    const currentVersion$ = this.referenceQuestionHttpService.fetch(referenceQuestionId);

    return combineLatest([currentVersion$, editHistory$, statusSnapshots$, reviewFeedback$]).pipe(
      map(([currentVersion, editHistory, statusSnapshots, reviewFeedback]) => {
        const editEntries = [currentVersion, ...editHistory]
          .map((entry, index) => {
            const e = entry as TableRowModel<'reference_questions_history'>;
            return {
              isCurrentVersion: index === 0,
              id: `edit-${e.id}-${index}`,
              type: 'edit' as const,
              timestamp: e.updated_at ?? e.created_at,
              user_id: e.updated_by ?? e.created_by,
              question_items: e.question_items ? (e.question_items as unknown as QuestionItemV2[]) : undefined,
            };
          })
          .toSorted(
            (a, b) => new Date(b.timestamp ?? Date.now()).getTime() - new Date(a.timestamp ?? Date.now()).getTime(),
          )
          .filter((entry, index, arr) => {
            if (index === 0) {
              return true;
            }
            return !isEqual(entry.question_items, arr[index - 1].question_items);
          });

        const allEntries: ReferenceQuestionHistoryEntry[] = [
          ...editEntries,

          ...statusSnapshots.map((snapshot) => ({
            id: `status-${snapshot.snapshot_id}`,
            type: 'status_change' as const,
            timestamp: snapshot.updated_at ?? snapshot.created_at,
            user_id: snapshot.updated_by ?? snapshot.created_by,
            question_items: snapshot.question_items
              ? (snapshot.question_items as unknown as QuestionItemV2[])
              : undefined,
            status: snapshot.status,
            status_reason: snapshot.status_reason,
          })),

          ...reviewFeedback.map((feedback) => ({
            id: `review-${feedback.action_id}`,
            type: 'review_feedback' as const,
            timestamp: feedback.created_at,
            user_id: feedback.user_id,
            question_items: feedback.question_items ?? undefined,
            action_type: feedback.action_type,
            feedback: feedback.feedback,
          })),
        ];

        return allEntries.sort(
          (a, b) => new Date(b.timestamp ?? Date.now()).getTime() - new Date(a.timestamp ?? Date.now()).getTime(),
        );
      }),
    );
  }

  /**
   * Retrieves comprehensive history for a derivative question including:
   * - Edit history from questions_history table
   * - Status change snapshots from question_snapshots table
   * - Review feedback with actions
   * @param questionId - The ID of the derivative question
   * @returns Observable with array of unified history entries, sorted by timestamp (newest first)
   */
  getDerivativeQuestionComprehensiveHistory(
    questionId: QuestionHttpModel['id'],
  ): Observable<ReferenceQuestionHistoryEntry[]> {
    const editHistory$ = this.questionHttpService.fetchEditHistory(questionId);
    const statusSnapshots$ = this.questionHttpService.fetchStatusSnapshots(questionId);
    const reviewFeedback$ = this.getQuestionFeedback(questionId);
    const currentVersion$ = this.questionHttpService.fetch(questionId);

    return combineLatest([currentVersion$, editHistory$, statusSnapshots$, reviewFeedback$]).pipe(
      map(([currentVersion, editHistory, statusSnapshots, reviewFeedback]) => {
        const editEntries = [currentVersion, ...editHistory]
          .map((entry, index) => {
            const e = entry as TableRowModel<'questions_history'>;
            return {
              isCurrentVersion: index === 0,
              id: `edit-${e.id}-${index}`,
              type: 'edit' as const,
              timestamp: e.updated_at ?? e.created_at,
              user_id: e.updated_by ?? e.author,
              question_items: e.question_items as unknown as QuestionItemV2[],
            };
          })
          .toSorted(
            (a, b) => new Date(b.timestamp ?? Date.now()).getTime() - new Date(a.timestamp ?? Date.now()).getTime(),
          )
          .filter((entry, index, arr) => {
            if (index === 0) {
              return true;
            }
            return !isEqual(entry.question_items, arr[index - 1].question_items);
          });

        const allEntries: ReferenceQuestionHistoryEntry[] = [
          ...editEntries,

          ...statusSnapshots.map((snapshot) => ({
            id: `status-${snapshot.snapshot_id}`,
            type: 'status_change' as const,
            timestamp: snapshot.created_at,
            user_id: snapshot.updated_by ?? snapshot.author,
            status: snapshot.status,
            status_reason: undefined,
            question_items: snapshot.question_items as unknown as QuestionItemV2[],
          })),

          ...reviewFeedback.map((feedback) => ({
            id: `review-${feedback.action_id}`,
            type: 'review_feedback' as const,
            timestamp: feedback.created_at,
            user_id: feedback.user_id,
            action_type: feedback.action_type,
            feedback: feedback.feedback,
            question_items: feedback.question_items,
          })),
        ];

        return allEntries.sort(
          (a, b) => new Date(b.timestamp ?? Date.now()).getTime() - new Date(a.timestamp ?? Date.now()).getTime(),
        );
      }),
      this.errorHandlerService.catchError(
        '[ReviewItemService]: Fetching derivative question comprehensive history failed',
        rethrowError(),
        {
          toast: (err) => ({
            title: 'Failed to load question history',
            description: (err as Error)?.message,
          }),
          context: { questionId },
        },
      ),
    );
  }
}
